import React from 'react';
import ArtistPicker from './ArtistPicker';
import MuxThumbnailFramePicker from './MuxThumbnailFramePicker';
import BattleDesignationFields from './BattleDesignationFields';
import useBattleDesignation from './useBattleDesignation';

const VideoModal = ({
  show,
  editingVideo,
  formData,
  artists,
  onSubmit,
  onClose,
  onFormChange,
  onArtistToggle,
}) => {
  const battle = useBattleDesignation(formData, onFormChange, artists);

  if (!show) return null;

  const handleFormSubmit = (e) => {
    if (battle.battleNeedsAttention) {
      e.preventDefault();
      return;
    }
    onSubmit(e);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      data-testid="video-modal"
    >
      <div className="bg-battle-gray rounded-lg max-w-2xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto mx-4 sm:mx-auto">
        <h2 className="text-2xl font-bold text-white mb-6">
          {editingVideo ? 'Edit Video' : 'Add New Video'}
        </h2>

        <form onSubmit={handleFormSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-400 text-sm mb-2">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => onFormChange({ ...formData, title: e.target.value })}
              className="input-field"
              required
              data-testid="video-title-input"
            />
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => onFormChange({ ...formData, description: e.target.value })}
              className="input-field"
              rows="4"
              data-testid="video-description-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-400 text-sm mb-2">Platform *</label>
              <select
                value={formData.platform}
                onChange={(e) => onFormChange({ ...formData, platform: e.target.value })}
                className="input-field"
                required
                data-testid="video-platform-select"
              >
                <option value="youtube">YouTube</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Status *</label>
              <select
                value={formData.status}
                onChange={(e) => onFormChange({ ...formData, status: e.target.value })}
                className="input-field"
                required
                data-testid="video-status-select"
              >
                <option value="public">Public</option>
                <option value="exclusive">Exclusive</option>
                <option value="unlisted">Unlisted</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-2">
              YouTube Video ID *
            </label>
            <input
              type="text"
              value={formData.platform_video_id}
              onChange={(e) =>
                onFormChange({ ...formData, platform_video_id: e.target.value })
              }
              className="input-field"
              placeholder="e.g., dQw4w9WgXcQ"
              required
              data-testid="video-platform-id-input"
            />
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-2">Thumbnail URL</label>
            <input
              type="url"
              value={formData.thumbnail_url}
              onChange={(e) =>
                onFormChange({ ...formData, thumbnail_url: e.target.value })
              }
              className="input-field"
              placeholder="https://..."
              data-testid="video-thumbnail-input"
            />
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-2">Price (USD)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.price}
              onChange={(e) =>
                onFormChange({ ...formData, price: e.target.value })
              }
              className="input-field"
              placeholder="Leave blank for free"
              data-testid="video-price-input"
            />
            <p className="text-gray-500 text-xs mt-1">
              Any video with a price requires purchase to watch, regardless of access level. Leave blank for free.
            </p>
          </div>

          {/* Frame picker — Mux only */}
          {formData.platform === 'mux' && (
            <MuxThumbnailFramePicker
              video={editingVideo}
              thumbnailTime={formData.thumbnail_time}
              onTimeChange={(time) =>
                onFormChange({ ...formData, thumbnail_time: time })
              }
            />
          )}

          <div>
            <label className="block text-gray-400 text-sm mb-2">Artists</label>
            <ArtistPicker
              artists={artists}
              selectedIds={formData.artist_ids}
              onToggle={onArtistToggle}
            />
          </div>

          <BattleDesignationFields formData={formData} battle={battle} />

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              data-testid="cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={battle.battleNeedsAttention}
              className={`btn-primary ${battle.battleNeedsAttention ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="save-video-btn"
            >
              {editingVideo ? 'Update' : 'Create'} Video
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VideoModal;
